/*
 * tq-roasting-cart — keeps roasting/packaging FEE lines in lock-step with
 * their coffee line (native roasting option; see assets/tq-roasting.js for the
 * PDP and assets/tq-catalog.js for the Café cuit catalog).
 *
 * The markers, set at add time:
 *   coffee line:  properties._tq_roasted = "<fingerprint of the choices>"
 *   fee line:     properties._tq_for     = "<coffee variant id>|<fingerprint>"
 *
 * On every cart change (quantity edit or line removal, cart page and drawer
 * both publish PUB_SUB_EVENTS.cartUpdate with the fresh cart state) each fee
 * line's expected quantity is the summed quantity of the coffee lines it
 * points at — zero when the coffee is gone, which removes the orphan. One
 * idempotent POST /cart/update.js fixes every drifted line at once; a second
 * pass computes no updates, so this can never loop. Keying on the underscore
 * markers (hidden from every cart surface) keeps it locale-independent.
 *
 * The fee lines' own steppers/remove buttons are suppressed server-side
 * (snippets/cart-drawer.liquid, sections/main-cart-items.liquid): the coffee
 * line is the single driver, this file is the enforcement.
 */
(() => {
  // Bare identifiers, not window.*: constants.js/pubsub.js declare PUB_SUB_EVENTS
  // and subscribe with top-level const/function — accessible across scripts,
  // but top-level const never becomes a window property.
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  let busy = false;

  function reconcile(items) {
    const updates = {};
    const roasted = {};
    items.forEach((item) => {
      const props = item.properties || {};
      if (props._tq_roasted && !props._tq_for) {
        const key = `${item.variant_id}|${props._tq_roasted}`;
        roasted[key] = (roasted[key] || 0) + item.quantity;
      }
    });
    items.forEach((item) => {
      const props = item.properties || {};
      if (!props._tq_for) return;
      const expected = roasted[props._tq_for] || 0;
      if (item.quantity !== expected) updates[item.key] = expected;
    });
    return updates;
  }

  // The cart-items element (cart page) / cart-drawer-items (drawer) knows the
  // right section names + selectors for this page; reuse them so the repaint
  // matches what assets/cart.js itself does after a quantity change.
  function sectionHost() {
    const el = document.querySelector('cart-drawer-items, cart-items');
    return el && typeof el.getSectionsToRender === 'function' && typeof el.getSectionInnerHTML === 'function'
      ? el
      : null;
  }

  async function run(event) {
    if (busy) return;
    try {
      let items = event && event.cartData && event.cartData.items;
      if (!items) {
        const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        items = (await res.json()).items;
      }
      const updates = reconcile(items || []);
      if (!Object.keys(updates).length) return;

      busy = true;
      const host = sectionHost();
      const body = { updates };
      if (host) {
        body.sections = host.getSectionsToRender().map((section) => section.section || section.id);
        body.sections_url = window.location.pathname;
      }
      const res = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return;
      const state = await res.json();
      if (host && state.sections) {
        host.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          if (!target) return;
          const toReplace = (section.selector && target.querySelector(section.selector)) || target;
          const html = state.sections[section.section || section.id];
          if (html) toReplace.innerHTML = host.getSectionInnerHTML(html, section.selector);
        });
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);
      }
    } catch (e) {
      /* leave the cart as-is; the next change re-runs the reconciliation */
    } finally {
      busy = false;
    }
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, run);

  // Initial pass: a fee line rendered server-side (.qtyc--fee) means the cart
  // may hold drift from a path with no JS (or an interrupted session) — check
  // once per page load, only when one is actually present.
  const initial = () => {
    if (document.querySelector('.qtyc--fee')) run();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initial);
  else initial();
})();
